import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedSunoPrompt } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface SunoPromptStore {
  savedPrompts: SavedSunoPrompt[]
  selectedPromptId: string | null

  // CRUD操作
  savePrompt: (prompt: SavedSunoPrompt) => Promise<void>
  updatePrompt: (prompt: SavedSunoPrompt) => Promise<void>
  deletePrompt: (id: string) => Promise<void>
  deleteAllPrompts: () => Promise<void>

  // 検索・フィルター
  searchPrompts: (query: string) => SavedSunoPrompt[]
  getPromptsByGenre: (genre: string) => SavedSunoPrompt[]
  getPromptsByType: (type: 'instrumental' | 'vocal' | 'hybrid') => SavedSunoPrompt[]
  getPromptsByTags: (tags: string[]) => SavedSunoPrompt[]
  getFavoritePrompts: () => SavedSunoPrompt[]

  // インポート・エクスポート
  exportPrompts: () => string
  importPrompts: (jsonString: string) => void
  exportAsText: (ids?: string[]) => string

  // グループ管理
  groupPrompts: (ids: string[], groupId: string) => void
  ungroupPrompts: (ids: string[]) => void
  getPromptsByGroup: (groupId: string) => SavedSunoPrompt[]

  // タグ管理
  addTagToPrompt: (id: string, tag: string) => void
  removeTagFromPrompt: (id: string, tag: string) => void
  getAllTags: () => string[]

  // 使用履歴
  recordUsage: (id: string, result: 'success' | 'partial' | 'failed', notes?: string) => void
}

export const useSunoPromptStore = create<SunoPromptStore>()(
  persist(
    (set, get) => ({
      savedPrompts: [],
      selectedPromptId: null,

      savePrompt: async (prompt) => {
        set((state) => ({
          savedPrompts: [...state.savedPrompts, prompt]
        }))
      },

      updatePrompt: async (prompt) => {
        set((state) => ({
          savedPrompts: state.savedPrompts.map(p =>
            p.id === prompt.id ? { ...prompt, updatedAt: new Date().toISOString() } : p
          )
        }))
      },

      deletePrompt: async (id) => {
        set((state) => ({
          savedPrompts: state.savedPrompts.filter(p => p.id !== id),
          selectedPromptId: state.selectedPromptId === id ? null : state.selectedPromptId
        }))
      },

      deleteAllPrompts: async () => {
        if (confirm('すべてのプロンプトを削除してもよろしいですか？この操作は取り消せません。')) {
          set({ savedPrompts: [], selectedPromptId: null })
        }
      },

      searchPrompts: (query) => {
        const { savedPrompts } = get()
        const lowerQuery = query.toLowerCase()
        return savedPrompts.filter(p =>
          p.title.toLowerCase().includes(lowerQuery) ||
          p.result.promptInfo.genre?.toLowerCase().includes(lowerQuery) ||
          p.result.promptInfo.summary?.toLowerCase().includes(lowerQuery) ||
          p.tags?.some(tag => tag.toLowerCase().includes(lowerQuery)) ||
          p.memo?.toLowerCase().includes(lowerQuery)
        )
      },

      getPromptsByGenre: (genre) => {
        const { savedPrompts } = get()
        return savedPrompts.filter(p => p.result.promptInfo.genre === genre)
      },

      getPromptsByType: (type) => {
        const { savedPrompts } = get()
        return savedPrompts.filter(p => p.result.promptInfo.type === type)
      },

      getPromptsByTags: (tags) => {
        const { savedPrompts } = get()
        return savedPrompts.filter(p =>
          tags.some(tag => p.tags?.includes(tag))
        )
      },

      getFavoritePrompts: () => {
        const { savedPrompts } = get()
        return savedPrompts.filter(p => p.favorite)
      },

      exportPrompts: () => {
        const { savedPrompts } = get()
        return JSON.stringify(savedPrompts, null, 2)
      },

      importPrompts: (jsonString) => {
        try {
          const imported = JSON.parse(jsonString)
          const prompts = Array.isArray(imported) ? imported : imported.savedPrompts || []

          const validPrompts = prompts
            .filter((p: any) => p.result && p.result.mainPrompt)
            .map((p: any) => ({
              ...p,
              id: p.id || `imported_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
              createdAt: p.createdAt || new Date().toISOString(),
              updatedAt: p.updatedAt || new Date().toISOString()
            }))

          set({ savedPrompts: validPrompts })
        } catch (error) {
          throw new Error('インポートデータの形式が不正です')
        }
      },

      exportAsText: (ids) => {
        const { savedPrompts } = get()
        const targetPrompts = ids
          ? savedPrompts.filter(p => ids.includes(p.id))
          : savedPrompts

        return targetPrompts.map(p => {
          const r = p.result
          return `
========================================
タイトル: ${p.title}
生成日時: ${p.createdAt}
ジャンル: ${r.promptInfo.genre}
タイプ: ${r.promptInfo.type}
文字数: ${r.promptInfo.characterCount}
品質: ${r.promptInfo.estimatedQuality}
========================================

【メインプロンプト】
${r.mainPrompt.fullPrompt}

キーワード: ${r.mainPrompt.keywords?.join(', ') || 'なし'}
強調要素: ${r.mainPrompt.emphasis?.join(', ') || 'なし'}

${r.negativePrompt ? `
【ネガティブプロンプト】
${r.negativePrompt.prompt}
` : ''}

${r.lyricsWithTags ? `
【タグ付き歌詞】
${r.lyricsWithTags.fullLyrics}

セクション数: ${r.lyricsWithTags.totalSections}
推定時間: ${r.lyricsWithTags.estimatedDuration}
` : ''}

${p.memo ? `
【メモ】
${p.memo}
` : ''}

${p.tags && p.tags.length > 0 ? `
【タグ】
${p.tags.join(', ')}
` : ''}

${p.worldId ? '🌍 世界観連携あり\n' : ''}
${p.characterId ? '👤 キャラクター連携あり\n' : ''}
${p.lyricsId ? '🎵 歌詞連携あり\n' : ''}
`
        }).join('\n\n')
      },

      groupPrompts: (ids, groupId) => {
        set((state) => ({
          savedPrompts: state.savedPrompts.map(p =>
            ids.includes(p.id) ? { ...p, groupId } : p
          )
        }))
      },

      ungroupPrompts: (ids) => {
        set((state) => ({
          savedPrompts: state.savedPrompts.map(p =>
            ids.includes(p.id) ? { ...p, groupId: undefined } : p
          )
        }))
      },

      getPromptsByGroup: (groupId) => {
        const { savedPrompts } = get()
        return savedPrompts.filter(p => p.groupId === groupId)
      },

      addTagToPrompt: (id, tag) => {
        set((state) => ({
          savedPrompts: state.savedPrompts.map(p =>
            p.id === id
              ? { ...p, tags: [...(p.tags || []), tag], updatedAt: new Date().toISOString() }
              : p
          )
        }))
      },

      removeTagFromPrompt: (id, tag) => {
        set((state) => ({
          savedPrompts: state.savedPrompts.map(p =>
            p.id === id
              ? { ...p, tags: p.tags?.filter(t => t !== tag) || [], updatedAt: new Date().toISOString() }
              : p
          )
        }))
      },

      getAllTags: () => {
        const { savedPrompts } = get()
        const tags = new Set<string>()
        savedPrompts.forEach(p => {
          p.tags?.forEach(tag => tags.add(tag))
        })
        return Array.from(tags).sort()
      },

      recordUsage: (id, result, notes) => {
        set((state) => ({
          savedPrompts: state.savedPrompts.map(p =>
            p.id === id
              ? {
                  ...p,
                  usageHistory: [
                    ...(p.usageHistory || []),
                    {
                      usedAt: new Date().toISOString(),
                      result,
                      notes
                    }
                  ],
                  updatedAt: new Date().toISOString()
                }
              : p
          )
        }))
      }
    }),
    {
      name: 'suno-prompt-storage',
      version: 1,
      storage: createIndexedDBStorage('suno-prompt-storage'),
      partialize: (state) => ({ savedPrompts: state.savedPrompts })
    }
  )
)